import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ProtectedShell } from "@/features/auth/ProtectedShell";
import { Panel } from "@/components/ui/Panel";
import { Metric } from "@/components/ui/Metric";
import { DataTable } from "@/components/ui/DataTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { Pagination } from "@/components/ui/Pagination";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/features/toast/ToastProvider";
import { useConfirm } from "@/features/confirm/ConfirmProvider";
import { formatDate, formatMoney } from "@bookboost/shared/format";

interface PayoutRow {
  id: string;
  user_id: string;
  deal_id: string | null;
  invoice_id: string | null;
  commission_total: number;
  cash_portion: number;
  equity_portion: number;
  currency: string;
  status: "approved" | "paid";
  earned_at: string | null;
  paid_at: string | null;
  users: { email: string; full_name: string | null; salary_currency: string | null } | null;
  deals: { deal_name: string | null } | null;
  invoices: { stripe_invoice_id: string } | null;
  paid_by_user: { email: string; full_name: string | null } | null;
}

const financeSearch = z.object({
  show: z.enum(["approved", "paid"]).optional(),
  page: z.coerce.number().int().min(1).optional(),
});

interface CurrencyTotal {
  currency: string;
  total: number;
  count: number;
}

export const Route = createFileRoute("/finance")({
  validateSearch: financeSearch,
  component: () => (
    <ProtectedShell requireRole={["admin", "finance"]}>
      <FinanceContent />
    </ProtectedShell>
  ),
});

// Matches the .export-link treatment used on /admin/commissions.
const payrollLinkStyle: React.CSSProperties = {
  marginLeft: "auto",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "var(--muted)",
  fontSize: 12.5,
  padding: "6px 10px",
  borderRadius: 6,
};

function FinanceContent() {
  const { show, page } = Route.useSearch();
  const view = show === "paid" ? "paid" : "approved";
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: ["finance", "queue", view, page],
    queryFn: () =>
      api.get<{
        rows: PayoutRow[];
        status: "approved" | "paid";
        total: number;
        page: number;
        pageSize: number;
        totalsByCurrency: CurrencyTotal[];
      }>("/api/finance/queue", {
        searchParams: { show: view, page },
      }),
  });

  // Summed across the whole queue server-side, not just the current page.
  const totals = data?.totalsByCurrency ?? [];

  const visibleIds = useMemo(() => (data?.rows ?? []).map((r) => r.id), [data]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = visibleIds.some((id) => selected.has(id));

  function toggleAll(checked: boolean) {
    if (checked) setSelected(new Set(visibleIds));
    else setSelected(new Set());
  }
  function toggleOne(id: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  const bulkMarkPaid = useMutation({
    mutationFn: (ids: string[]) => api.post<{ ok: true; updated: number }>("/api/payouts/bulk-mark-paid", { ids }),
    onSuccess: (res) => {
      toast.ok(`Marked ${res.updated} commission${res.updated === 1 ? "" : "s"} paid.`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["finance"] });
      qc.invalidateQueries({ queryKey: ["admin"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't mark paid."),
  });

  const bulkMarkUnpaid = useMutation({
    mutationFn: (ids: string[]) => api.post<{ ok: true; updated: number }>("/api/payouts/bulk-mark-unpaid", { ids }),
    onSuccess: (res) => {
      toast.ok(`Reverted ${res.updated} commission${res.updated === 1 ? "" : "s"} to earned.`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["finance"] });
      qc.invalidateQueries({ queryKey: ["admin"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't revert."),
  });

  async function onBulkMarkPaid() {
    const ids = [...selected];
    if (ids.length === 0) return;
    const ok = await confirm({
      title: "Mark commissions paid",
      prompt: `Mark ${ids.length} commission${ids.length === 1 ? "" : "s"} as paid? Cash side becomes payable on the next payroll run.`,
      confirmLabel: "Mark paid",
    });
    if (ok) bulkMarkPaid.mutate(ids);
  }
  async function onBulkMarkUnpaid() {
    const ids = [...selected];
    if (ids.length === 0) return;
    const ok = await confirm({
      title: "Revert to earned",
      prompt: `Revert ${ids.length} commission${ids.length === 1 ? "" : "s"} back to earned? Only do this if payroll did not actually run.`,
      tone: "destructive",
      confirmLabel: "Revert",
    });
    if (ok) bulkMarkUnpaid.mutate(ids);
  }

  return (
    <>
      <h1>{view === "paid" ? "Recent payouts" : "Pending payouts"}</h1>

      <Panel>
        <nav style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Link
            to="/finance"
            search={{ show: undefined }}
            style={{ fontWeight: view === "approved" ? 600 : 400 }}
            aria-current={view === "approved" ? "page" : undefined}
            onClick={() => setSelected(new Set())}
          >
            Approved · ready to pay
          </Link>
          <Link
            to="/finance"
            search={{ show: "paid" }}
            style={{ fontWeight: view === "paid" ? 600 : 400 }}
            aria-current={view === "paid" ? "page" : undefined}
            onClick={() => setSelected(new Set())}
          >
            Paid history
          </Link>
          {/* Plain anchor so the sb-* cookies flow with the download. */}
          <a href={`/api/export/payroll.csv?status=${view}`} style={payrollLinkStyle}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M7 2v7m0 0l-3-3m3 3l3-3M2 12h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Payroll CSV
          </a>
        </nav>
      </Panel>

      {totals.length > 0 && (
        <div className={`grid cols-${Math.min(totals.length, 4)}`}>
          {totals.map((t) => (
            <Metric
              key={t.currency}
              label={view === "paid" ? `Paid total · ${t.currency}` : `Pending · ${t.currency}`}
              value={formatMoney(t.total, t.currency)}
              sub={`${t.count} commission${t.count === 1 ? "" : "s"}`}
              tone={view === "approved" ? "accent" : "default"}
            />
          ))}
        </div>
      )}

      {someSelected && (
        <Panel variant="ok">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <span>
              <strong>{selected.size}</strong> selected
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              {view === "approved" ? (
                <button
                  className="primary"
                  disabled={bulkMarkPaid.isPending}
                  onClick={onBulkMarkPaid}
                >
                  {bulkMarkPaid.isPending ? "Marking…" : "Mark selected paid"}
                </button>
              ) : (
                <button
                  style={{ color: "var(--err)", borderColor: "var(--err)" }}
                  disabled={bulkMarkUnpaid.isPending}
                  onClick={onBulkMarkUnpaid}
                >
                  {bulkMarkUnpaid.isPending ? "Reverting…" : "Revert to earned"}
                </button>
              )}
              <button type="button" onClick={() => setSelected(new Set())}>
                Clear
              </button>
            </div>
          </div>
        </Panel>
      )}

      <Panel padding="tight">
        {isLoading ? (
          <p className="lede">Loading…</p>
        ) : error ? (
          <p className="err">{error instanceof Error ? error.message : "Failed to load."}</p>
        ) : !data || data.rows.length === 0 ? (
          <EmptyState
            icon="payouts"
            title={view === "paid" ? "No paid commissions yet" : "Nothing waiting on finance"}
            body={
              view === "paid"
                ? "Mark commissions as paid to see them here."
                : "Once admins approve commissions, they show up here ready to be marked paid."
            }
          />
        ) : (
          <DataTable>
            <table className="table-cards">
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      aria-label="Select all visible"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = !allSelected && someSelected;
                      }}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </th>
                  <th>Earned</th>
                  <th>Rep</th>
                  <th>Deal</th>
                  <th>Status</th>
                  <th>Cash</th>
                  <th>Equity</th>
                  <th>Total</th>
                  {view === "paid" && <th>Paid</th>}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select commission ${r.id}`}
                        checked={selected.has(r.id)}
                        onChange={(e) => toggleOne(r.id, e.target.checked)}
                      />
                    </td>
                    <td data-label="Earned" className="muted">{formatDate(r.earned_at)}</td>
                    <td data-label="Rep">{r.users?.full_name ?? r.users?.email ?? "—"}</td>
                    <td data-label="Deal">{r.deals?.deal_name ?? "—"}</td>
                    <td data-label="Status"><StatusBadge status={r.status} /></td>
                    <td data-label="Cash">{formatMoney(r.cash_portion, r.currency)}</td>
                    <td data-label="Equity">{formatMoney(r.equity_portion, r.currency)}</td>
                    <td data-label="Total"><strong>{formatMoney(r.commission_total, r.currency)}</strong></td>
                    {view === "paid" && (
                      <td data-label="Paid" className="muted">
                        {formatDate(r.paid_at)}
                        {r.paid_by_user && (
                          <div style={{ fontSize: 11 }}>by {r.paid_by_user.full_name ?? r.paid_by_user.email}</div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTable>
        )}
        {data && data.total > 0 && (
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={(p) => navigate({ search: { show, page: p } })}
          />
        )}
      </Panel>
    </>
  );
}
